/**
 * App shell — sticky topbar, capped content column, and the active-task
 * toolbar. The bar shows while I have a running OR paused service (and never
 * on the WO Detail route — we ARE on the job there): pausing keeps the bar,
 * swapped to Resume, so I can pick the work back up without hunting for it.
 * It names the SERVICE; the task underneath is the backend's business.
 * It's fixed to the bottom, so the content column adds matching bottom
 * padding to clear it. Vehicle + plate only — never a customer name.
 *
 * <AuthGuard> wraps the Outlet so the dev-panel "auth bypass" toggle has
 * something real to override.
 */
import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@vollgas/shared/utils'
import { useCompleteTask, useDoList, usePauseTask, useStartTask } from '@/hooks/useWorkshop'
import { BottomActionBar } from '@/components/BottomActionBar'
import { useActionToast } from '@/components/ActionToast'
import { Topbar } from '@/components/Topbar'
import { AuthGuard } from '@/auth/AuthGuard'
import { RouteTransition } from '@/transitions/RouteTransition'
import { usePresence } from '@vollgas/shared/hooks'
import type { DoListResponse, DoListServiceState, DoListWorkOrder } from '@/apis/types'

/** First of MY tasks in the given state, walking the WO → service → task tree.
 *  The SERVICE is what comes back: the bar's part actions name one, because
 *  a part hangs off the service. */
function findMyService(doList: DoListResponse | undefined, state: DoListServiceState) {
  for (const wo of doList?.workOrders ?? []) {
    const svc = wo.services.find((s) => s.mine && s.state === state)
    if (svc) return { wo, svc }
  }
  return null
}

/** "Audi A4 Avant · W-1834 LM" — vehicle + plate, never a customer name. */
function vehiclePlateLine(wo: DoListWorkOrder): string {
  return [wo.vehicleDisplayName, wo.vehiclePlate].filter(Boolean).join(' · ')
}

export function AppShell() {
  const { data } = useDoList()
  const pause = usePauseTask()
  const start = useStartTask()
  const complete = useCompleteTask()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useActionToast()
  const onWoDetail = location.pathname.startsWith('/work-orders/')

  // Same contract as the two routes: optimistic, so a failure is a bar that
  // already flipped and flipped back — it has to say why.
  const startTask = (taskId: string) => {
    toast.clear()
    start.mutate(taskId, { onError: (error) => toast.reportError(error, () => startTask(taskId)) })
  }
  const pauseTask = (taskId: string) => {
    toast.clear()
    pause.mutate(taskId, { onError: (error) => toast.reportError(error, () => pauseTask(taskId)) })
  }

  // The bar shows while I have a running OR paused task (not on the WO detail).
  // Prefer a running task; fall back to a paused one so pausing keeps the bar.
  const active = findMyService(data, 'in_progress') ?? findMyService(data, 'paused')
  const showBar = Boolean(active) && !onWoDetail
  const bar = usePresence(showBar, 200)
  // Hold the last running task so the bar still has data through its slide-out.
  const lastActive = useRef(active)
  if (active) lastActive.current = active
  const shown = active ?? lastActive.current

  return (
    <div className="flex min-h-screen flex-col bg-glass-base">
      <Topbar />
      {/* 1100px → 1280px (2026-09-22): the WO detail page grew a second
          column (the notes panel beside the job) that needed the room. */}
      <main className={cn('mx-auto w-full max-w-[1280px] flex-1 px-6 pt-7', showBar ? 'pb-28' : 'pb-8')}>
        <AuthGuard>
          <RouteTransition />
        </AuthGuard>
      </main>
      {bar.mounted && shown && (
        <BottomActionBar
          taskName={shown.svc.name}
          vehicleLine={vehiclePlateLine(shown.wo)}
          loggedMinutes={shown.svc.loggedMinutes}
          runningSince={shown.svc.runningSince}
          serviceId={shown.svc.id}
          workOrderId={shown.wo.id}
          onOpen={() => navigate(`/work-orders/${shown.wo.id}`)}
          paused={shown.svc.state === 'paused'}
          onPause={() => pauseTask(shown.svc.id)}
          onResume={() => startTask(shown.svc.id)}
          timerPending={start.isPending || pause.isPending}
          onDone={() => complete.mutate({ workOrderId: shown.wo.id, serviceId: shown.svc.id })}
          className={
            bar.status === 'closing'
              ? 'motion-safe:animate-bottombar-out motion-reduce:animate-fade-out'
              : 'motion-safe:animate-bottombar-in motion-reduce:animate-fade-in'
          }
        />
      )}
    </div>
  )
}
